/**
 * PURPOSE: Reglas puras de la interfaz para PONER a un deportista en una serie (agregar a la prueba y mover de serie).
 * RESPONSIBILITIES: Carriles (pista: carril obligatorio, uno por carril; 800/1500 `shared`: dos), quién ocupa cada carril,
 *   qué hacer con un carril lleno (reemplazar / intercambiar → parámetros del servidor), siguiente orden en campo, si una fila
 *   ya tiene resultado, qué series se agregan o quitan y las etiquetas de rama/nivel. El servidor repite todas estas
 *   validaciones: aquí solo se evita ofrecer opciones que él va a rechazar y se explica el motivo.
 * COLLABORATORS: components/AddAthleteModal.jsx, components/AddTeamModal.jsx, components/LanePicker.jsx,
 *   components/MoveEntryModal.jsx, components/PruebaDetailPanel.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/placement.js
 */
export const LANE_NUMBERS = Object.freeze(Array.from({ length: 12 }, (_, index) => index + 1));

export const usesLanes = (prueba) => prueba?.laneMode === "lane" || prueba?.laneMode === "shared";
export const laneCapacity = (prueba) => (prueba?.laneMode === "shared" ? 2 : 1);

/** Filas de la serie en ese carril. */
export const occupantsOfLane = (round, lane, { excludeId = null } = {}) =>
  (Array.isArray(round?.entries) ? round.entries : []).filter(
    (entry) => Number(entry.lane) === Number(lane) && entry.id !== excludeId
  );

export const isLaneFull = (prueba, round, lane, options) =>
  occupantsOfLane(round, lane, options).length >= laneCapacity(prueba);

/** Orden de salida de quien se agrega al final: máximo + 1. */
export const nextOrderOf = (round) =>
  Math.max(0, ...(Array.isArray(round?.entries) ? round.entries : []).map((entry) => Number(entry.order) || 0)) + 1;

/** La fila ya tiene marca, estado (OK/DNS/DNF/DQ…), intentos o alturas saltadas: al moverla, el resultado viaja con ella. */
export const entryHasResult = (entry) =>
  Boolean(entry) &&
  ((entry.markValue !== null && entry.markValue !== undefined) ||
    Boolean(entry.status) ||
    (Array.isArray(entry.attempts) && entry.attempts.length > 0) ||
    (Array.isArray(entry.heights) && entry.heights.some((height) => Boolean(height?.attempts))));

/** "marca 11.92", "estado DNS" o "intentos registrados": qué se pierde o se mueve con la fila. */
export const describeEntryResult = (entry) => {
  if (!entryHasResult(entry)) return "";
  if (entry.mark) return `marca ${entry.mark}`;
  if (entry.status) return `estado ${entry.status}`;
  return "intentos registrados";
};

/** Motivo por el que no se quita ni se reemplaza una fila con resultado en una sub-prueba de combinada (lo exige el servidor). */
export const COMBINED_RESULT_REASON =
  "En las combinadas no se quita a un atleta con resultado: márcalo DNS (abandono) para que la combinada lo refleje.";

/**
 * Carril elegido al agregar: libre, o lleno (1 por carril; 800/1500, 2). Con el carril lleno el operador elige `choice.mode`:
 * "replace" (el ocupante sale de la serie) o "swap" (el ocupante queda en la serie sin carril), a quién (si hay dos) y, si el
 * ocupante tiene resultado, confirmarlo. Devuelve { full, occupants, target, missing, params }: `params` va tal cual al
 * servicio (replace/swap, replaceRowId/swapRowId, confirmResult). El servidor repite todas estas reglas.
 */
export const resolveLaneChoice = ({ prueba, round, lane, choice = {}, inCombined = false }) => {
  const none = { full: false, occupants: [], target: null, missing: "", params: {} };
  if (!usesLanes(prueba) || !lane) return none;
  const occupants = occupantsOfLane(round, lane);
  if (occupants.length < laneCapacity(prueba)) return { ...none, occupants };
  const mode = choice.mode === "replace" || choice.mode === "swap" ? choice.mode : null;
  const target = occupants.length === 1 ? occupants[0] : occupants.find((item) => item.id === choice.targetRowId) || null;
  const withResult = Boolean(target) && entryHasResult(target);
  let missing = "";
  if (!mode) missing = "El carril está ocupado: elige reemplazar o intercambiar, u otro carril.";
  else if (!target) missing = "Elige a quién reemplazar o intercambiar.";
  else if (withResult && mode === "replace" && inCombined) missing = COMBINED_RESULT_REASON;
  else if (withResult && !choice.confirmResult) missing = "Confirma que la fila del carril ya tiene resultado.";
  const params = mode && target ? { [mode]: true, [`${mode}RowId`]: target.id, ...(withResult ? { confirmResult: true } : {}) } : {};
  return { full: true, occupants, target, withResult, mode, missing, params };
};

export const BRANCH_LABELS = Object.freeze({ F: "Damas", M: "Varones", X: "Mixta" });
export const branchLabel = (code) => BRANCH_LABELS[code] || "Sin rama";
export const LEVEL_LABELS = Object.freeze({ NV: "Noveles", AZ: "Avanzados" });
export const levelLabel = (code) => LEVEL_LABELS[code] || code || "";

/** "Serie 2" o, si es la única, la etiqueta de la serie. */
export const roundName = (round) => round?.label || (round?.type === "final" ? "Final" : round?.number ? `Serie ${round.number}` : "Serie");

/** Una serie "terminada" (oficial) no admite agregar ni mover filas. */
export const isRoundLocked = (round) => round?.status === "done";

/** Texto corto de quién ocupa un lugar: nombre (sigla) o la sigla del equipo. */
export const occupantLabel = (entry) => {
  const name = entry?.participant?.name || "";
  const abrev = entry?.participant?.institution?.abrev || "";
  return abrev && name ? `${name} (${abrev})` : name || abrev || "Sin nombre";
};

/** Formatos con series que se agregan o quitan (pista): carreras, marcha y postas. Campo y combinadas (madre) no. */
export const SERIES_FORMATS = Object.freeze(["CPT", "MAR", "PST"]);
export const supportsSeries = (prueba) => SERIES_FORMATS.includes(prueba?.format);
export const seriesRoundsOf = (rounds) => (Array.isArray(rounds) ? rounds : []).filter((round) => round.type === "heat" || round.type === "single");

/** Una serie se quita solo si está vacía, no está terminada y no es la única (la final nunca). */
export const isRemovableSeries = (round, rounds) =>
  Boolean(round) &&
  (round.type === "heat" || round.type === "single") &&
  !(Array.isArray(round.entries) && round.entries.length) &&
  !isRoundLocked(round) &&
  seriesRoundsOf(rounds).length > 1;

/** Series a las que puede ir una fila: del mismo tipo que la de origen y distintas de ella. */
export const moveTargetRounds = (rounds, sourceRound) =>
  (Array.isArray(rounds) ? rounds : []).filter((round) => sourceRound && round.id !== sourceRound.id && round.type === sourceRound.type);

/** Motivos por los que un acreditado no puede agregarse (mismos textos que la pestaña Categorías; sin importar el cliente HTTP). */
export const INELIGIBLE_LABELS = Object.freeze({
  ENTRY_NOT_ACTIVE: "Inscripción retirada",
  NOT_APPROVED: "Acreditación no aprobada",
  NOT_ATHLETE: "No está acreditado como deportista",
  INSTITUTION_NOT_PARTICIPATING: "Su universidad no participa en el deporte",
});
